import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  AngularEditorComponent,
  AngularEditorConfig,
} from "@kolkov/angular-editor";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UploadDropzoneComponent } from "../../upload-dropzone/upload-dropzone.component";
import { ServiceStatus, StorageTarget } from "../../../enums";
import { Job, Service } from "../../../models";
import {
  AgencyService,
  AuthenticationService,
  DialogService,
  JobsService,
} from "../../../services";
import { UploadService } from "../../../services/upload.service";
import { ServicesService } from "../../../services/services.service";
import * as Sentry from "@sentry/angular-ivy";
import { reportTicketError, reportTicketMessage } from "../../../helpers";

@Component({
  selector: "prism-image-upload",
  templateUrl: "./image-upload.component.html",
  styleUrls: ["./image-upload.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploadComponent implements OnInit, OnDestroy {
  storageDestination: StorageTarget;
  briefEditorConfig: AngularEditorConfig;
  closeServiceUploader: boolean;
  fileCount: number;
  serviceHandled: boolean;
  uploadLimited: any;
  allowedFile: string;
  loadingInProgress: boolean;
  serviceId: string;
  serviceForm: FormGroup;
  aiRestricted: boolean;
  isLuxEngine: boolean;
  private subDestroyer$ = new Subject<void>();

  @Input() service: Service;
  @Input() productId: string;
  @Input() serviceIndex: number;
  @Input() parentJob: Job;
  @Input() onlyEdit: boolean;
  @Output() serviceBoxClosed: EventEmitter<{
    uploadComponent: any;
    onlyEdit: boolean;
    destination: string;
    service: any;
    cancelled: boolean;
  }> = new EventEmitter<{
    uploadComponent: any;
    onlyEdit: any;
    destination: any;
    service: any;
    cancelled: any;
  }>();
  @Output() closeAllDialogHandler: EventEmitter<{
    cancelled: boolean;
    fileCount: number;
    serviceId: string;
  }> = new EventEmitter<{
    cancelled: boolean;
    fileCount: number;
    serviceId: string;
  }>();
  @ViewChild("uploadDropzone", { static: true })
  uploadDropzoneComponent: UploadDropzoneComponent;
  @ViewChild("angularEditor", { static: false })
  angularEditor: AngularEditorComponent;

  constructor(
    private formBuilder: FormBuilder,
    private jobsService: JobsService,
    private agencyService: AgencyService,
    private dialogService: DialogService,
    private uploadService: UploadService,
    private servicesService: ServicesService,
    private authService: AuthenticationService,
    private cdr: ChangeDetectorRef
  ) {
    this.briefEditorConfig = {
      enableToolbar: false,
      placeholder: "Enter brief",
      spellcheck: true,
      maxHeight: "auto",
      editable: true,
      showToolbar: false,
      customClasses: [
        {
          name: "brief",
          class: "brief-editor",
        },
      ],
    };
    this.onlyEdit = false;
    this.closeServiceUploader = false;
    this.fileCount = 0;
    this.serviceHandled = false;
    this.uploadLimited = this.uploadService.uploadLimited$;
    this.allowedFile = "image";
    this.loadingInProgress = false;
    this.serviceForm = this.formBuilder.group({
      id: new FormControl(null),
      service: new FormControl(null),
      processingRequired: new FormControl(true),
      aiOnly: new FormControl(false),
      DISPLAY: new FormControl(""),
      SNAKE_CASE: new FormControl(""),
      status: new FormControl(""),
      brief: new FormControl({ value: null, disabled: false }),
      numOfFinals: new FormControl(null, [
        Validators.min(1),
        Validators.pattern(/^\d*?$/),
      ]),
    });
    this.fileCount = 0;
  }

  ngOnInit() {
    const job = this.jobsService.selectedJob;
    if (job.agency) {
      this.agencyService.getAgency(job.agency).subscribe((agency) => {
        this.serviceForm
          .get("brief")
          .setValue(JSON.parse(agency?.notes.replace(/\\n/g, `<br>`)) ?? "");
      });
    }
    this.serviceForm.get("DISPLAY").setValue(this.service.DISPLAY);
    this.serviceForm.get("SNAKE_CASE").setValue(this.service.SNAKE_CASE);

    // Once the backend has determined whether this service is AI-processed,
    // lock the AI Only toggle to that value instead of letting the member change it.
    // Processing Required stays editable regardless, so members can always switch
    // a service between processed/no-processing.
    this.aiRestricted = typeof this.service.isLuxEngine === "boolean";
    if (this.aiRestricted) {
      const noProcessing = this.service.status === ServiceStatus.noProcessing;
      this.serviceForm.get("processingRequired")?.setValue(!noProcessing);
      this.serviceForm.get("aiOnly")?.setValue(noProcessing ? false : this.service.isLuxEngine);
      // formControlName's [disabled] template binding doesn't reliably disable
      // mat-slide-toggle, so lock the control directly via the reactive form API.
      this.serviceForm.get("aiOnly")?.disable();
    }

    // LuxEngine members must always run Zero Touch when Processing Required
    // is on for Image services — lock it on unless the backend has already
    // made an AI decision for this service (aiRestricted takes precedence).
    this.authService.isLuxEngine$
      .pipe(takeUntil(this.subDestroyer$))
      .subscribe((isLuxEngine) => {
        this.isLuxEngine = isLuxEngine;
        if (
          isLuxEngine &&
          !this.aiRestricted &&
          this.serviceForm.get("processingRequired")?.getRawValue()
        ) {
          this.serviceForm.get("aiOnly")?.setValue(true);
          this.serviceForm.get("aiOnly")?.disable();
        }
        this.cdr.markForCheck();
      });

    // Set initial state of brief form control based on aiOnly value
    const aiOnlyValue = this.serviceForm.get("aiOnly").value;
    if (aiOnlyValue) {
      this.briefFormControl.disable();
    }
  }

  ngOnDestroy() {
    this.subDestroyer$.next();
    this.subDestroyer$.complete();
  }

  startUpload() {
    const required = this.serviceForm.get("processingRequired").getRawValue();
    if (required) {
      this.onProcessServiceClicked();
    } else {
      this.onSaveServiceClicked();
    }
  }

  handleProcessingRequiredSelection(required: boolean) {
    this.storageDestination = required
      ? StorageTarget.PROCESSOR
      : StorageTarget.MEMBER;
    if (!required) {
      // When processing is not required, AI Only must be false and locked
      this.serviceForm.get("aiOnly").setValue(false);
      if (this.isLuxEngine && !this.aiRestricted) {
        this.serviceForm.get("aiOnly")?.enable();
      }
    } else if (this.isLuxEngine && !this.aiRestricted) {
      // LuxEngine: processing always implies Zero Touch — force it on and lock it.
      this.serviceForm.get("aiOnly").setValue(true);
      this.serviceForm.get("aiOnly")?.disable();
    }
    this.serviceForm.markAsTouched();
  }

  handleAiOnlySelection(aiOnlySelected: boolean) {
    const processingRequiredSelected = this.serviceForm.get("processingRequired").getRawValue();
    if (!processingRequiredSelected && aiOnlySelected) {
      // Prevent enabling AI Only when processing is not required
      this.serviceForm.get("aiOnly").setValue(false);
      return;
    }
    
    // Disable/enable the brief form control based on AI Only selection
    if (aiOnlySelected) {
      this.briefFormControl.disable();
    } else {
      this.briefFormControl.enable();
    }
    
    this.serviceForm.markAsTouched();
  }

  setFileCount(count: number) {
    this.fileCount = count;
  }

  onSaveServiceClicked() {
    this.loadingInProgress = true;
    this.updateInputActiveState(false);

    if (!this.serviceForm.get("processingRequired").value) {
      this.serviceForm.controls["brief"].setValue("");
      this.serviceForm.get("numOfFinals").setValidators([]);
      this.serviceForm.controls["numOfFinals"].setValue(0);
    }
    this.uploadService.removeUploadLimited(this.service.id);
    this.serviceForm.get("status").setValue(ServiceStatus.noProcessing);
    this.closeServiceUploaderHandler(false, StorageTarget.MEMBER);
    if (!this.onlyEdit) {
      this.closeDialog(false);
    }
  }

  onProcessServiceClicked() {
    this.loadingInProgress = true;
    this.updateInputActiveState(false);
    this.uploadService.removeUploadLimited(this.service.id);
    this.serviceForm.get("status").setValue(ServiceStatus.uploadInProgress);
    console.debug('[DEBUG onProcessServiceClicked] serviceId:', this.service.id, '| version:', this.service.version, '| ticketId:', this.service.ticketId, '| SNAKE_CASE:', this.service.SNAKE_CASE);
    Sentry.addBreadcrumb({
      category: "ticket",
      message: "onProcessServiceClicked branch decision",
      level: "info",
      data: {
        serviceId: this.service.id,
        serviceType: this.service.SNAKE_CASE,
        serviceVersion: this.service.version,
        existingTicketId: this.service.ticketId,
      },
    });
    this.closeServiceUploaderHandler(false, StorageTarget.PROCESSOR);

    // if (this.service.version === "v2") {
      if (!this.service.ticketId) {
        console.debug('[DEBUG onProcessServiceClicked] no ticketId — calling createTicket, processor job will fire after upload completes');
        this.createTicket();
      } else if (this.service.SNAKE_CASE === "image_processing") {
        const briefData = {
          brief: this.serviceForm.get("brief").getRawValue(),
          numOfFinals: this.serviceForm.get("numOfFinals").getRawValue(),
          address: this.parentJob.address,
        };
        this.servicesService
          .createSubTicket(this.service, false, briefData)
          .subscribe({
            next: () => null,
            error: (e) => {
              console.error("Error creating sub-ticket:", e);
              reportTicketError(
                "onProcessServiceClicked.createSubTicket",
                this.service,
                e,
                { existingTicketId: this.service.ticketId }
              );
            },
          });
      } else {
        // service.version is "v2", ticketId is already set, and SNAKE_CASE
        // isn't "image_processing" — neither createTicket nor createSubTicket
        // fires here, so no ticket request is sent for this service at all.
        reportTicketMessage(
          "onProcessServiceClicked",
          this.service,
          "warning",
          "onProcessServiceClicked skipped ticket creation: ticketId already set and service is not image_processing",
          { existingTicketId: this.service.ticketId }
        );
      }
    // } else {
    //   // service.version isn't "v2" at click time (unset/stale on a new or
    //   // in-flight service) — no ticket request is sent for this service at
    //   // all, and until now this branch left no trace anywhere.
    //   reportTicketMessage(
    //     "onProcessServiceClicked",
    //     this.service,
    //     "warning",
    //     "onProcessServiceClicked skipped ticket creation: service.version is not v2",
    //     { serviceVersion: this.service.version }
    //   );
    //   this.closeDialog(false);
    // }
  }

  onCancelClicked() {
    this.loadingInProgress = true;
    this.updateInputActiveState(false);
    this.uploadService.removeUploadLimited(this.service.id);
    this.closeServiceUploaderHandler(true, null);
    this.closeDialog(true);
  }

  closeServiceUploaderHandler(
    cancelled: boolean = false,
    destination: StorageTarget
  ) {
    try {
      this.serviceForm.disable();
      this.closeServiceUploader = true;
      this.serviceId = this.service.id;
      this.serviceForm.get("id").setValue(this.serviceId);

      if (
        this.serviceForm.get("status").value !== ServiceStatus.uploadInProgress
      ) {
        this.serviceForm.get("status").setValue(ServiceStatus.uploadInProgress);
      }

      this.serviceForm.updateValueAndValidity();
      this.serviceHandled = true;

      if (this.onlyEdit) {
        if (!cancelled) {
          this.uploadDropzoneComponent.startUpload(
            this.parentJob.id,
            destination,
            this.serviceId
          );
        }
        this.dialogService.closeAllDialogs();
      } else {
        if (cancelled) {
          this.serviceForm.reset();
          this.serviceForm.get("id").setValue(this.serviceId);
          this.serviceForm.get("status").setValue(ServiceStatus.draft);
          this.serviceForm.get("DISPLAY").setValue(this.service.DISPLAY);
          this.serviceForm.get("SNAKE_CASE").setValue(this.service.SNAKE_CASE);
          this.serviceForm.updateValueAndValidity();
        }

        this.serviceBoxClosed.emit({
          uploadComponent: this.uploadDropzoneComponent,
          service: {
            ...this.serviceForm.getRawValue(),
            version: this.service.version,
          },
          onlyEdit: this.onlyEdit,
          destination,
          cancelled,
        });
      }
      this.loadingInProgress = false;
      this.updateInputActiveState(true);
    } catch (e) {
      console.error("Error closing upload modal:", e);
      this.loadingInProgress = false;
      this.updateInputActiveState(true);
    }
  }

  createTicket() {
    const formData: { [key: string]: any } = {};
    Object.entries(this.serviceForm.controls).forEach(([key, val]) => {
      formData[key] = val?.getRawValue() || null;
    });
    formData["address"] = this.parentJob.address;

    // Zero Touch services always send "Zero Touch" as the brief, even if an
    // agency note was pre-filled into the brief field.
    if (formData["aiOnly"]) {
      formData["brief"] = "Zero Touch";
    }

    this.servicesService.createTicket(this.service, formData);
    this.closeDialog(false);
  }

  updateInputActiveState(active: boolean) {
    if (active) {
      this.briefFormControl.enable();
      this.numOfFinalsFormControl.enable();
    } else {
      this.briefFormControl.disable();
      this.numOfFinalsFormControl.disable();
    }
  }

  closeDialog(cancelled: boolean) {
    this.closeAllDialogHandler.emit({
      cancelled: cancelled,
      fileCount: this.fileCount,
      serviceId: this.service.id,
    });
  }

  get briefFormControl() {
    return this.serviceForm.get("brief") as FormControl;
  }

  get numOfFinalsFormControl() {
    return this.serviceForm.get("numOfFinals") as FormControl;
  }
}
